import { useState, useEffect, useRef, useCallback } from 'react';
import { formatTime } from '../../utils/timeUtils';
import { filterPresentationTimeNodes } from '../../utils/presentationUtils';

const PresentationPanel = ({ 
  presentationTimeNodes = [],
  currentTime = 0,
  duration = 0,
  onSeek,
  isMobile = false
}) => {
  // 移除了对 DataShareContext 的依赖
  const [isOpen, setIsOpen] = useState(false);
  const [allPresentations, setAllPresentations] = useState([]);
  const [currentHighlightedNode, setCurrentHighlightedNode] = useState(null);
  const [isClosing, setIsClosing] = useState(false); // 新增：用于控制关闭动画
  const [hoveredImage, setHoveredImage] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const autoCloseTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  
  // 处理悬停事件，避免影响面板交互
  const handleImageMouseEnter = (slideUrl, e) => {
    setHoveredImage(slideUrl);
    // 获取当前图片元素的位置
    const rect = e.currentTarget.getBoundingClientRect();
    
    // 设置悬停预览位置为在当前图片的左侧，同时确保不超出屏幕边界
    const previewWidth = 300; // 增加预览图片宽度以获得更清晰的视图
    const adjustedX = rect.left - previewWidth - 10 < 0 ? rect.right + 10 : rect.left - previewWidth - 10;
    
    setHoverPosition({ x: adjustedX, y: rect.top });
  };
  
  const handleImageMouseMove = (e) => {
    if (!hoveredImage) return;
    
    // 获取当前图片元素的位置
    const rect = e.currentTarget.getBoundingClientRect();
    
    // 更新悬停预览位置为在当前图片的左侧，同时确保不超出屏幕边界
    const previewWidth = 300; // 增加预览图片宽度以获得更清晰的视图
    const adjustedX = rect.left - previewWidth - 10 < 0 ? rect.right + 10 : rect.left - previewWidth - 10;
    
    setHoverPosition({ x: adjustedX, y: rect.top });
  };
  
  const handleImageMouseLeave = () => {
    setHoveredImage(null);
  };
  
  // 当面板关闭时，同时关闭预览框
  useEffect(() => {
    if (!isOpen) {
      setHoveredImage(null);
    }
  }, [isOpen]);
  


  // 获取所有课件时间节点并按时间排序
  useEffect(() => {
    if (!presentationTimeNodes || presentationTimeNodes.length === 0) {
      setAllPresentations([]);
      return;
    }

    // 过滤课件时间节点
    const filteredNodes = filterPresentationTimeNodes(presentationTimeNodes, duration);
    
    // 按时间排序
    const sortedNodes = [...filteredNodes].sort((a, b) => a.time - b.time);
    
    setAllPresentations(sortedNodes);
  }, [presentationTimeNodes, duration]);

  // 找到小于当前时间的最后一张课件切换记录
  const getCurrentPresentationNode = useCallback(() => {
    if (!allPresentations.length) return null;
    
    // 找到小于当前时间的最后一张课件切换记录
    for (let i = allPresentations.length - 1; i >= 0; i--) {
      const node = allPresentations[i];
      // 只考虑课件切换事件
      if (node.type === 'presentation_switch' && node.time <= currentTime) {
        return node;
      }
    }
    
    return null;
  }, [allPresentations, currentTime]);
  
  // 当时间或课件数据变化时，更新当前高亮的节点
  useEffect(() => {
    const highlightedNode = getCurrentPresentationNode();
    setCurrentHighlightedNode(highlightedNode);
  }, [currentTime, allPresentations, getCurrentPresentationNode]);

  // 自动关闭逻辑
  useEffect(() => {
    if (!isOpen) {
      // 面板关闭时清除定时器
      if (autoCloseTimerRef.current) {
        clearInterval(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
      return;
    }

    // 面板打开时启动定时器
    const checkAutoClose = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      
      // 如果7秒内无操作，则自动关闭
      if (timeSinceLastActivity >= 3000) {
        // 先关闭预览框
        setHoveredImage(null);
            
        // 触发关闭动画
        setIsClosing(true);
        // 延迟真正关闭面板，以播放关闭动画
        setTimeout(() => {
          setIsOpen(false);
          setIsClosing(false);
        }, 300);
      }
    };

    // 设置定时器每秒检查一次
    autoCloseTimerRef.current = setInterval(checkAutoClose, 1000);

    return () => {
      // 清除定时器
      if (autoCloseTimerRef.current) {
        clearInterval(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [isOpen]);

  // 更新最后活动时间
  const updateLastActivity = () => {
    lastActivityRef.current = Date.now();
  };

  // 监听鼠标移动和点击事件来更新活动时间
  useEffect(() => {
    if (!isOpen) return;

    const handleActivity = () => {
      updateLastActivity();
    };

    // 添加事件监听器
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('mousedown', handleActivity);
    document.addEventListener('keydown', handleActivity);
    document.addEventListener('touchstart', handleActivity);

    return () => {
      // 移除事件监听器
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('mousedown', handleActivity);
      document.removeEventListener('keydown', handleActivity);
      document.removeEventListener('touchstart', handleActivity);
    };
  }, [isOpen]);

  // 如果是移动端或者没有课件数据，不显示
  if (isMobile || allPresentations.length === 0) {
    return null;
  }

  return (
    <>
      {/* 悬浮图标 */}
      <button 
        style={{
          position: 'fixed',
          right: '60px',
          bottom: '60px',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: '#fb4643',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          transition: 'all 0.3s ease',
          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)'
        }}
        onClick={() => {
          // 如果是关闭面板，则先关闭预览框
          if (isOpen) {
            setHoveredImage(null);
          }
          
          setIsOpen(!isOpen);
          // 更新活动时间
          updateLastActivity();
        }}
        title="课件切换"
        onMouseMove={updateLastActivity}
        onTouchStart={updateLastActivity}
      >
        <svg t="1766388744372" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2473" width="24" height="24"><path d="M758.690909 707.490909H372.363636c-18.618182 0-37.236364 18.618182-37.236363 37.236364s18.618182 37.236364 37.236363 37.236363h390.981819c18.618182 0 37.236364-18.618182 37.236363-37.236363 0-23.272727-23.272727-37.236364-41.890909-37.236364zM758.690909 567.854545H605.090909c-18.618182 0-37.236364 18.618182-37.236364 37.236364s18.618182 37.236364 37.236364 37.236364h158.254546c18.618182 0 37.236364-18.618182 37.236363-37.236364 0-23.272727-23.272727-37.236364-41.890909-37.236364zM614.4 372.363636c0-79.127273-60.509091-139.636364-139.636364-139.636363h-232.727272v381.672727c0 18.618182 18.618182 37.236364 37.236363 37.236364s37.236364-18.618182 37.236364-37.236364V512h158.254545c74.472727 0 139.636364-60.509091 139.636364-139.636364z m-302.545455-69.818181h158.254546c37.236364 0 65.163636 27.927273 65.163636 65.163636s-27.927273 65.163636-65.163636 65.163636h-158.254546v-130.327272z" p-id="2474" fill="#ffffff"></path><path d="M847.127273 46.545455h-665.6c-69.818182 0-130.327273 60.509091-130.327273 130.327272v665.6c0 69.818182 60.509091 130.327273 130.327273 130.327273h665.6c74.472727 0 130.327273-55.854545 130.327272-130.327273V176.872727c0-69.818182-60.509091-130.327273-130.327272-130.327272z m60.509091 791.272727c0 32.581818-23.272727 55.854545-55.854546 55.854545H186.181818c-32.581818 0-55.854545-23.272727-55.854545-55.854545V176.872727c0-32.581818 23.272727-55.854545 55.854545-55.854545h665.6c32.581818 0 55.854545 23.272727 55.854546 55.854545V837.818182z" p-id="2475" fill="#ffffff"></path></svg>
      </button>

      {/* 课件切换面板 */}
      {(isOpen || isClosing) && (
        <div 
          style={{
            position: 'fixed',
            right: '60px',
            bottom: '110px',
            width: '300px',
            maxHeight: '600px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            overflow: 'hidden',
            animation: isClosing ? 'slideOutDown 0.3s ease forwards' : 'slideInUp 0.3s ease'
          }}
        >
          <div 
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid #eee',
              fontWeight: 'bold',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
            onMouseMove={updateLastActivity}
            onTouchStart={updateLastActivity}
          >
            <span>课件切换</span>
            <button 
              onClick={() => {
                // 先关闭预览框
                setHoveredImage(null);
                
                // 触发关闭动画
                setIsClosing(true);
                // 延迟真正关闭面板，以播放关闭动画
                setTimeout(() => {
                  setIsOpen(false);
                  setIsClosing(false);
                }, 300);
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                color: '#999'
              }}
            >
              ×
            </button>
          </div>
          <div 
            style={{
              maxHeight: '500px',
              overflowY: 'auto'
            }}
            onMouseMove={updateLastActivity}
            onTouchStart={updateLastActivity}
          >
            {allPresentations.map((node, index) => (
              <div 
                key={`node-${index}`}
                onClick={() => {
                  onSeek(node.time);
                  // 不再关闭面板
                  // 更新活动时间
                  updateLastActivity();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 16px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f5f5f5',
                  transition: 'background-color 0.2s',
                  // 如果是当前课件，则标红
                  backgroundColor: currentHighlightedNode && node.time === currentHighlightedNode.time ? 'rgba(255, 0, 0, 0.1)' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = currentHighlightedNode && node.time === currentHighlightedNode.time ? 'rgba(255, 0, 0, 0.2)' : '#f5f5f5';
                  // 更新活动时间
                  updateLastActivity();
                }}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentHighlightedNode && node.time === currentHighlightedNode.time ? 'rgba(255, 0, 0, 0.1)' : 'white'}
                onMouseMove={updateLastActivity}
                onTouchStart={updateLastActivity}
              >
                {node.slideUrl && (
                  <img 
                    src={node.slideUrl}
                    alt={node.title}
                    style={
                      {
                        width: '80px',
                        height: '60px',
                        objectFit: 'cover',
                        marginRight: '12px',
                        borderRadius: '4px'
                      }
                    }
                    onMouseEnter={(e) => handleImageMouseEnter(node.slideUrl, e)}
                    onMouseLeave={handleImageMouseLeave}
                    onMouseMove={handleImageMouseMove}
                  />
                )}
                <div style={{
                  flex: 1,
                  minWidth: 0
                }}>
                  <div 
                    style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={node.title}
                  >
                    {node.title}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#999',
                    marginTop: '2px'
                  }}>
                    {formatTime(node.time)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 悬停预览 */}
      {hoveredImage && (
        <div
          style={{
            position: 'fixed',
            left: hoverPosition.x,
            top: hoverPosition.y,
            zIndex: 9999,
            pointerEvents: 'none',
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <img
            src={hoveredImage}
            alt="预览"
            style={{
              width: '300px',
              height: '225px',
              objectFit: 'contain',
            }}
          />
        </div>
      )}
      
      {/* 动画样式 */}
      <style jsx>{`
        @keyframes slideInUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutDown {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(20px);
            opacity: 0;
          }
        }
      `}</style>
    </>
  );
};

export default PresentationPanel;